// main.js's own strings (the boot status line, the GPS help cards, the quit
// box, the HUD rows + key hints, the beam-in / ⤳ Beam outcomes, the phone
// briefing's outcome lines, the graphics-reset note) and the static index.html
// text outside the landing (main.html.*, wired with data-i18n). Every key is
// 'main.*'; the en value is the text the code used before i18n. Tone: punchy +
// playful, informal "du", Swiss spelling (ss, never ß). World data (place /
// OSM names, the bake's numbers) is never in here.
import { register } from '../i18n.js';

register({
  en: {
    // the status line (#status, setStatus)
    'main.status.connFail': 'connection failed: {err} — is the server running?',
    'main.status.connFailFull': 'connection failed: {err} — try again later',
    'main.status.noMeta': 'bake {bake}: meta.json unavailable — is the bake complete?',
    'main.status.clientReload': 'this world needs a newer game client — reloading…',
    'main.status.clientBehind': 'This world needs game client {min} or newer; this page runs {cur}. The server\'s game client must be rebuilt (dashboard: Rebuild client) — this page reloads by itself once it is.',
    'main.status.bakeOld': 'This world was built for game client {need}; this client reads worlds from {min} on. The world must be rebuilt (dashboard: Build, then Make live).',
    'main.status.loading': 'loading {name}…',
    'main.status.loadingWorld': 'loading the world…',
    'main.status.lost': 'connection lost (server restarted?) — reloading…',
    'main.status.connected': 'connected — bake {bake} · client build {build} · {nb} buildings · {nt} trees',
    // the GPS help cards (gpsDiagnose → #gps-help / the phone's card)
    'main.gps.policy.title': 'Location is switched off by this site\'s own headers',
    'main.gps.policy.s1': 'This page is served with a Permissions-Policy response header whose geolocation allowlist is empty — geolocation=() disables the API for every origin, including this page itself. That is why no prompt appeared, and why no setting on this phone or in this browser can turn it back on.',
    'main.gps.policy.s2': 'The fix belongs to whoever runs the web server in front of the game: the header has to allow the site itself — geolocation=(self) instead of geolocation=(). It lives in the nginx add_header Permissions-Policy line, or Apache\'s Header always set Permissions-Policy line, whichever sits in front of this page.',
    'main.gps.policy.s3': 'The same header normally also blocks gyroscope and magnetometer (tilt-look and the compass) and microphone (proximity voice chat), so allowing all of them with (self) brings the three back together.',
    'main.gps.policy.s4': 'Until then the game plays fine without GPS — the stick walks you anywhere.',
    'main.gps.cert.title': 'GPS can\'t work on this address — its certificate isn\'t trusted',
    'main.gps.cert.s1': 'You opened the game on its local network address, which serves a self-signed certificate. Phones refuse to hand location to a page whose certificate they don\'t trust, so the request was refused instantly — that is why no permission prompt appeared, and why no setting on this page can change it.',
    'main.gps.cert.s2': 'Ask the game owner to set Server → Public URL in the dashboard, then open that https link (or scan its QR code) instead of this one.',
    'main.gps.cert.s3': 'Or install this server\'s certificate as trusted on the phone. Until then the game plays fine without GPS — the stick walks you anywhere.',
    'main.gps.blocked.title': 'Location is blocked for this site',
    'main.gps.blocked.s1': 'Android Chrome: leave fullscreen, tap the 🔒 or ⓘ left of the address bar → Permissions → Location → Allow, then reload the page.',
    'main.gps.blocked.s2': 'iPhone Safari: tap aA in the address bar → Website Settings → Location → Allow. Also check Settings → Privacy & Security → Location Services → Safari.',
    'main.gps.blocked.s3': 'Come back here and tap Try again.',
    'main.gps.os.title': 'Your phone isn\'t handing out location at all',
    'main.gps.os.s1': 'Switch Location on in the phone\'s quick settings (swipe down from the top edge).',
    'main.gps.os.s2': 'Give the browser app itself the Location permission — Android: Settings → Apps → your browser → Permissions → Location; iPhone: Settings → Privacy & Security → Location Services → your browser.',
    'main.gps.os.s3': 'Step outdoors or next to a window: the very first fix needs to see sky.',
    'main.gps.os.s4': 'Then tap Try again.',
    'main.gps.insecure.title': 'This page is not a secure origin — location is switched off',
    'main.gps.insecure.s1': 'Browsers hand out location only to https pages. This one was loaded over plain http, so geolocation is unavailable before any prompt can happen.',
    'main.gps.insecure.s2': 'Open the game over its https link — the owner sets Server → Public URL in the dashboard and shares that address (or its QR code).',
    'main.gps.insecure.s3': 'Nothing else is affected: the stick walks you anywhere without GPS.',
    'main.gps.unsupported.title': 'This browser has no geolocation',
    'main.gps.unsupported.s1': 'navigator.geolocation is missing here, so nothing on this page can ask for a position — this is the browser, not a permission.',
    'main.gps.unsupported.s2': 'Try the phone\'s stock browser (Chrome or Safari) on an https address — the owner sets Server → Public URL in the dashboard.',
    'main.gps.unsupported.s3': 'GPS only decides where you start and whether the avatar follows you; the stick walks you anywhere without it.',
    'main.gps.transient.title': 'No fix yet — the phone cannot see enough satellites',
    'main.gps.transient.s1': 'Go outdoors, or stand right next to a window: a first fix needs open sky and can take up to a minute after a cold start.',
    'main.gps.transient.s2': 'The request is still running, so a fix that arrives late will still place you — nothing is broken and there is nothing to switch on.',
    'main.gps.transient.s3': 'Tap Try again if a minute passes with no change.',
    // gps.err, as the briefing's GPS line shows it
    'main.gpsErr.denied': 'denied',
    'main.gpsErr.noFix': 'no fix',
    'main.gpsErr.timeout': 'timeout',
    'main.gpsErr.error': 'error',
    'main.gpsErr.notCh': 'not CH',
    'main.gpsErr.outside': 'outside world',
    // the phone briefing's outcome lines (_introStatus)
    'main.intro.iosFs': '⛶ no full screen in iPhone Safari — Share → "Add to Home Screen", then start GEO-VR from there',
    'main.intro.gpsDiag': '📍 {title} — tap 📍 in the game for the fix',
    'main.intro.gpsFail': '📍 GPS unavailable — tap 📍 in the game for help',
    'main.intro.gpsErr': '📍 GPS: {err} — tap 📍 in the game to retry',
    'main.intro.gpsOn': '📍 GPS on — you walk where you walk',
    'main.intro.gpsOnAcc': '📍 GPS on — you walk where you walk (±{m} m)',
    'main.intro.compass': '🧭 compass live — the view faces where you hold the phone',
    'main.intro.gyroPolicy': '📱 this site\'s headers block the motion sensors — drag to look. The site owner has to allow gyroscope=(self), magnetometer=(self) in its Permissions-Policy header',
    'main.intro.gyroDenied': '📱 motion sensors denied — drag on the screen to look around',
    'main.intro.gyroNone': '📱 no motion sensors — drag on the screen to look around',
    'main.intro.gyroLive': '📱 gyro live, no compass — heading is relative; the stick turns you',
    'main.intro.gyroWait': '📱 no motion data yet — drag on the screen to look around',
    'main.intro.wakePolicy': '🔆 this site\'s headers block the screen wake lock — your phone will dim on its own timer (Permissions-Policy screen-wake-lock=(self))',
    'main.intro.wakeNone': '🔆 this browser cannot hold the screen awake — raise the phone\'s screen timeout',
    'main.intro.wakeFailed': '🔆 the screen wake lock was refused (battery saver?) — your phone may dim',
    // "📍 Beam to my position" (beamToFix)
    'main.fix.none': '📍 no GPS fix yet — tap the Map instead',
    'main.fix.outside': '📍 your position is outside this world — tap the Map instead',
    'main.fix.label': 'your GPS position',
    'main.fix.waiting': '📍 waiting for a GPS fix…',
    // the Map pin after a ⟳ reload here
    'main.pin.here': 'you were here',
    // the quit box (askQuit)
    'main.quit.game': 'Leave the world?',
    'main.quit.map': 'Leave the Map?',
    'main.quit.gameSub': 'You leave the world and full screen; the page starts over.',
    'main.quit.mapSub': 'Back to the start page, out of full screen.',
    // the ⤳ Beam's outcomes (enterAt) + the beam-in sub-line
    'main.beam.inWorld': 'already in the world',
    'main.beam.noMode': 'pick a mode on the landing page first',
    'main.beam.busy': 'already beaming…',
    // the beam flight's input (main.js flightHint): a press held until landed, the skip hold
    'main.flight.mapAfter': 'Map after landing',
    'main.flight.briefAfter': 'briefing after landing',
    'main.flight.holdSkip': 'hold Space to skip',
    'main.flight.landing': 'Landing',
    'main.flight.alt': '{m} m a.s.l.',
    'main.flight.water': '{lake} {c} °C',
    'main.wait.alt': 'You land at {m} m a.s.l.',
    'main.wait.peak': 'Nearest summit: {name}, {m} m — {km} km {dir}',
    'main.wait.highest': 'Highest around: {name}, {m} m — {km} km {dir}',
    'main.wait.summits': '{n} named summits within {km} km',
    'main.wait.lake': '{name} — {km} km {dir}',
    'main.wait.place': '{name} — {km} km {dir}',
    'main.dir.n': 'N', 'main.dir.ne': 'NE', 'main.dir.e': 'E', 'main.dir.se': 'SE',
    'main.dir.s': 'S', 'main.dir.sw': 'SW', 'main.dir.w': 'W', 'main.dir.nw': 'NW',
    // the device bench line (?bench=1, main.js benchBanner); {phase} = entry | walk | jet
    'main.bench.run': 'BENCH · {phase} — hands off, it drives itself',
    'main.bench.done': 'BENCH done — report {id} sent. The ops panel shows it: world → Telemetry → Device bench.',
    'main.bench.noRoute': 'bench: no route for this world (bench/{world}.json) — normal play',
    'main.beam.again': 'beaming again…',
    'main.beam.vrFail': 'VR session failed: {err} — still on the Map',
    'main.beam.abandoned': 'beam abandoned',
    'main.beam.refused': 'beam refused by the server (out of bounds, combat, or a seat within 2 s) — tried twice, still on the Map',
    'main.beam.timeout': 'no answer from the game server in {s} s, twice — still on the Map',
    'main.beam.offline': 'not connected to the game server — still on the Map',
    // the HUD (desktop #hud, the VR HUD)
    'main.hud.down': '💀 DOWN — frozen {s}s',
    'main.hud.dizzy': '💨 DIZZY {s}s',
    'main.hud.jet': '🚀 JET {v} km/h · AGL {h} m',
    'main.hud.bike': '🏍️ {v} km/h · {surf}',
    'main.hud.road': 'road',
    'main.hud.offroad': 'offroad',
    'main.hud.auto': 'AUTO',
    'main.hud.manual': 'MANUAL',
    'main.hud.indL': '⬅ IND',
    'main.hud.indR': 'IND ➡',
    'main.hud.funicular': '🚋 funicular nearby — walk in',
    'main.hud.chair': '🚡 chairlift here',
    'main.hud.cabin': '🚠 cabin here',
    'main.hud.orClick': 'or click it',
    'main.hud.monoClick': '<kbd>3</kbd> monocular, then click it',
    'main.hud.keysBikeAuto': '<kbd>W</kbd>/<kbd>S</kbd> throttle/brake <kbd>←</kbd>/<kbd>→</kbd> indicators <kbd>Q</kbd> manual <kbd>B</kbd> dismount',
    'main.hud.keysBikeMan': '<kbd>W</kbd>/<kbd>S</kbd> throttle/brake <kbd>A</kbd>/<kbd>D</kbd> steer <kbd>Q</kbd> auto <kbd>B</kbd> dismount',
    'main.hud.keysGlide': 'fly where you look <kbd>S</kbd> brakes',
    'main.hud.keysCombat': '<kbd>1</kbd> Rifle <kbd>2</kbd> Sniper <kbd>3</kbd> Monocular <kbd>Q</kbd>/<kbd>MMB</kbd> Switch',
    'main.hud.keyMic': '<kbd>M</kbd> Mic',
    'main.hud.keyMicMuted': '<kbd>M</kbd> Mic (muted)',
    'main.hud.keyFire': '<kbd>LMB</kbd> Fire',
    'main.hud.keyInspect': '<kbd>LMB</kbd> Inspect',
    // the key-hint pages (keyhints.js keyHintGroups): one string = one binding
    'main.kh.d.move': '<kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd> Move',
    'main.kh.d.look': '<kbd>Mouse</kbd> Look',
    'main.kh.d.sprint': '<kbd>Shift</kbd> Sprint',
    'main.kh.d.jump': '<kbd>Space</kbd> Jump',
    'main.kh.d.climb': '<kbd>E</kbd> Climb',
    'main.kh.d.zoom': '<kbd>Wheel</kbd> Zoom',
    'main.kh.d.map': '<kbd>RMB</kbd>/<kbd>N</kbd> Map',
    'main.kh.d.bike': '<kbd>B</kbd> Bike',
    'main.kh.d.help': '<kbd>?</kbd> Help',
    'main.kh.p.walk': 'stick: walk/run',
    'main.kh.p.walkTurn': 'stick ↕ walk ↔ turn',
    'main.kh.p.lookPoint': 'point phone: look',
    'main.kh.p.lookBoth': 'point/drag: look',
    'main.kh.p.lookDrag': 'drag: look',
    'main.kh.p.jump': '🚀 jump',
    'main.kh.p.zoom': 'pinch/slider: zoom',
    'main.kh.p.mapTilt': 'tip flat: Map',
    'main.kh.p.mapBtn': '🗺 Map',
    'main.kh.p.lock': '🔒 hold view',
    'main.kh.p.inspect': '◎ what is there',
    'main.kh.p.fire': 'FIRE · ⟳ weapon',
    'main.kh.p.gps': '📍 follow GPS',
    'main.kh.p.glide': 'jet-suit: flies where you look',
    'main.kh.v.move': 'L-stick move (full: run)',
    'main.kh.v.turn': 'R-stick ↔ turn',
    'main.kh.v.jump': 'R-click jump/jet',
    'main.kh.v.climb': 'grips climb',
    'main.kh.v.zoom': 'R-stick ↕ zoom',
    'main.kh.v.map': 'L-trigger Map',
    'main.kh.v.point': 'R-trigger: what is there',
    'main.kh.v.menu': 'A menu',
    'main.kh.v.bike': 'X bike',
    'main.vr.keysBikeAuto': 'L-stick ↕ throttle · ↔ flick indicator · Y manual · X get off · A menu',
    'main.vr.keysBikeMan': 'L-stick ↕ throttle ↔ steer · Y auto-follow · X get off · A menu',
    'main.vr.keysGlide': 'L-stick back = climb · forward = dive · ↔ steers · head only looks · A menu',
    'main.vr.zonePoint': 'R-trigger',
    'main.vr.mic': 'Y mic',
    'main.vr.micMuted': 'Y mic (muted)',
    // the graphics-reset note (#gl-reset, watchContext)
    'main.gl.restoring': 'graphics reset — restoring…',
    'main.gl.lost': 'graphics reset — not restored',
    'main.gl.restored': 'graphics restored — anything missing?',
    // index.html static text outside the landing (data-i18n)
    'main.html.beaming': '⤳ beaming…',
    'main.html.mapTitle': 'Map (RMB / N)',
    'main.html.map2d': '2D Map (right click / N)',
    'main.html.help': 'How it works',
    'main.html.me': 'Centre on me',
    'main.html.quit': 'Exit',
    'main.html.exit': '⏻ Exit',
    'main.html.mapClose': 'Close (RMB / N / Esc)',
    'main.html.mtbTitle': 'MTB: S-scale colours, MTB + bike routes',
    'main.html.amen': '🚰 Amenities',
    'main.html.amenTitle': 'Benches, fountains, toilets, shelters …',
    'main.html.north': 'North-up — tap for heading-up',
    'main.html.zoomIn': 'Zoom in',
    'main.html.zoomOut': 'Zoom out',
    'main.html.inspect': 'What the binoculars see',
    'main.html.stay': 'Cancel',
    'main.html.leave': 'Exit',
    'main.html.helpQ': 'How it works (?)',
    'main.html.briefing': 'Briefing',
    'main.html.close': 'Close',
    'main.html.closeEsc': 'Close (Esc)',
    'main.html.beamFix': '📍 Beam to my position',
    'main.html.gotIt': 'Got it',
    'main.html.reloadHere': '⟳ reload here',
  },
  de: {
    'main.status.connFail': 'Verbindung fehlgeschlagen: {err} — läuft der Server?',
    'main.status.connFailFull': 'Verbindung fehlgeschlagen: {err} — versuch es später nochmal',
    'main.status.noMeta': 'Bake {bake}: meta.json fehlt — ist der Bake fertig?',
    'main.status.clientReload': 'diese Welt braucht einen neueren Spiel-Client — lade neu…',
    'main.status.clientBehind': 'Diese Welt braucht Spiel-Client {min} oder neuer; diese Seite läuft mit {cur}. Der Spiel-Client des Servers muss neu gebaut werden (Dashboard: Rebuild client) — danach lädt die Seite von selbst neu.',
    'main.status.bakeOld': 'Diese Welt wurde für Spiel-Client {need} gebaut; dieser Client liest Welten ab {min}. Die Welt muss neu gebaut werden (Dashboard: Build, dann Make live).',
    'main.status.loading': 'lade {name}…',
    'main.status.loadingWorld': 'lade die Welt…',
    'main.status.lost': 'Verbindung weg (Server neu gestartet?) — lade neu…',
    'main.status.connected': 'verbunden — Bake {bake} · Client-Build {build} · {nb} Gebäude · {nt} Bäume',
    'main.gps.policy.title': 'Der Standort ist durch die Header dieser Seite selbst abgeschaltet',
    'main.gps.policy.s1': 'Diese Seite kommt mit einem Permissions-Policy-Header, dessen geolocation-Allowlist leer ist — geolocation=() sperrt die API für jeden Origin, auch für diese Seite selbst. Darum kam keine Abfrage, und darum kann keine Einstellung auf diesem Handy oder in diesem Browser sie wieder einschalten.',
    'main.gps.policy.s2': 'Beheben kann das nur, wer den Webserver vor dem Spiel betreibt: Der Header muss die Seite selbst erlauben — geolocation=(self) statt geolocation=(). Er steht in der nginx-Zeile add_header Permissions-Policy oder in Apaches Zeile Header always set Permissions-Policy, je nachdem, was vor dieser Seite läuft.',
    'main.gps.policy.s3': 'Derselbe Header sperrt meist auch gyroscope und magnetometer (Umschauen per Neigen und den Kompass) sowie microphone (Sprachchat in der Nähe) — wer alle mit (self) erlaubt, holt alle drei zusammen zurück.',
    'main.gps.policy.s4': 'Bis dahin läuft das Spiel auch ohne GPS bestens — mit dem Stick kommst du überallhin.',
    'main.gps.cert.title': 'GPS geht auf dieser Adresse nicht — ihrem Zertifikat wird nicht vertraut',
    'main.gps.cert.s1': 'Du hast das Spiel über seine lokale Netzwerkadresse geöffnet, und die liefert ein selbstsigniertes Zertifikat. Handys geben einer Seite, deren Zertifikat sie nicht vertrauen, keinen Standort — die Anfrage wurde sofort abgelehnt. Darum kam keine Berechtigungsabfrage, und darum kann keine Einstellung auf dieser Seite etwas daran ändern.',
    'main.gps.cert.s2': 'Bitte den Besitzer des Spiels, im Dashboard Server → Public URL zu setzen, und öffne dann diesen https-Link (oder scanne seinen QR-Code) statt dieser Adresse.',
    'main.gps.cert.s3': 'Oder installiere das Zertifikat dieses Servers auf dem Handy als vertrauenswürdig. Bis dahin läuft das Spiel auch ohne GPS bestens — mit dem Stick kommst du überallhin.',
    'main.gps.blocked.title': 'Der Standort ist für diese Seite blockiert',
    'main.gps.blocked.s1': 'Android Chrome: Vollbild verlassen, auf 🔒 oder ⓘ links neben der Adressleiste tippen → Berechtigungen → Standort → Zulassen, dann die Seite neu laden.',
    'main.gps.blocked.s2': 'iPhone Safari: in der Adressleiste auf aA tippen → Website-Einstellungen → Standort → Erlauben. Prüf auch Einstellungen → Datenschutz & Sicherheit → Ortungsdienste → Safari.',
    'main.gps.blocked.s3': 'Komm hierher zurück und tipp auf Nochmal.',
    'main.gps.os.title': 'Dein Handy rückt überhaupt keinen Standort raus',
    'main.gps.os.s1': 'Schalt den Standort in den Schnelleinstellungen des Handys ein (vom oberen Rand nach unten wischen).',
    'main.gps.os.s2': 'Gib der Browser-App selbst die Standort-Berechtigung — Android: Einstellungen → Apps → dein Browser → Berechtigungen → Standort; iPhone: Einstellungen → Datenschutz & Sicherheit → Ortungsdienste → dein Browser.',
    'main.gps.os.s3': 'Geh nach draussen oder ans Fenster: Der allererste Fix muss den Himmel sehen.',
    'main.gps.os.s4': 'Dann tipp auf Nochmal.',
    'main.gps.insecure.title': 'Diese Seite ist kein sicherer Origin — der Standort ist abgeschaltet',
    'main.gps.insecure.s1': 'Browser geben den Standort nur an https-Seiten heraus. Diese hier kam über einfaches http, darum gibt es keine Geolocation, bevor überhaupt eine Abfrage kommen kann.',
    'main.gps.insecure.s2': 'Öffne das Spiel über seinen https-Link — der Besitzer setzt im Dashboard Server → Public URL und teilt diese Adresse (oder ihren QR-Code).',
    'main.gps.insecure.s3': 'Sonst ist nichts betroffen: Mit dem Stick kommst du auch ohne GPS überallhin.',
    'main.gps.unsupported.title': 'Dieser Browser kennt keine Geolocation',
    'main.gps.unsupported.s1': 'navigator.geolocation fehlt hier, also kann nichts auf dieser Seite nach einer Position fragen — das liegt am Browser, nicht an einer Berechtigung.',
    'main.gps.unsupported.s2': 'Probier den Standardbrowser des Handys (Chrome oder Safari) auf einer https-Adresse — der Besitzer setzt sie im Dashboard unter Server → Public URL.',
    'main.gps.unsupported.s3': 'GPS entscheidet nur, wo du startest und ob der Avatar dir folgt; mit dem Stick kommst du auch ohne überallhin.',
    'main.gps.transient.title': 'Noch kein Fix — das Handy sieht zu wenige Satelliten',
    'main.gps.transient.s1': 'Geh nach draussen oder stell dich direkt ans Fenster: Ein erster Fix braucht freien Himmel und kann nach einem Kaltstart bis zu einer Minute dauern.',
    'main.gps.transient.s2': 'Die Anfrage läuft noch — ein Fix, der spät kommt, setzt dich trotzdem richtig hin. Nichts ist kaputt, und du musst nichts einschalten.',
    'main.gps.transient.s3': 'Tipp auf Nochmal, wenn eine Minute lang nichts passiert.',
    'main.gpsErr.denied': 'verweigert',
    'main.gpsErr.noFix': 'kein Fix',
    'main.gpsErr.timeout': 'Zeitüberschreitung',
    'main.gpsErr.error': 'Fehler',
    'main.gpsErr.notCh': 'nicht in der Schweiz',
    'main.gpsErr.outside': 'ausserhalb der Welt',
    'main.intro.iosFs': '⛶ kein Vollbild in iPhone-Safari — Teilen → «Zum Home-Bildschirm», dann GEO-VR von dort starten',
    'main.intro.gpsDiag': '📍 {title} — tipp im Spiel auf 📍 für die Lösung',
    'main.intro.gpsFail': '📍 GPS geht nicht — tipp im Spiel auf 📍 für Hilfe',
    'main.intro.gpsErr': '📍 GPS: {err} — tipp im Spiel auf 📍 für einen neuen Versuch',
    'main.intro.gpsOn': '📍 GPS an — du läufst, wo du läufst',
    'main.intro.gpsOnAcc': '📍 GPS an — du läufst, wo du läufst (±{m} m)',
    'main.intro.compass': '🧭 Kompass live — die Ansicht schaut dorthin, wohin du das Handy hältst',
    'main.intro.gyroPolicy': '📱 die Header dieser Seite sperren die Bewegungssensoren — zum Umschauen ziehen. Der Betreiber der Seite muss gyroscope=(self), magnetometer=(self) in seinem Permissions-Policy-Header erlauben',
    'main.intro.gyroDenied': '📱 Bewegungssensoren verweigert — zieh auf dem Bildschirm, um dich umzuschauen',
    'main.intro.gyroNone': '📱 keine Bewegungssensoren — zieh auf dem Bildschirm, um dich umzuschauen',
    'main.intro.gyroLive': '📱 Gyro live, kein Kompass — die Richtung ist relativ; der Stick dreht dich',
    'main.intro.gyroWait': '📱 noch keine Bewegungsdaten — zieh auf dem Bildschirm, um dich umzuschauen',
    'main.intro.wakePolicy': '🔆 die Header dieser Seite verbieten, den Bildschirm wach zu halten — dein Handy dimmt nach seinem eigenen Timer (Permissions-Policy screen-wake-lock=(self))',
    'main.intro.wakeNone': '🔆 dieser Browser kann den Bildschirm nicht wach halten — stell die Bildschirm-Zeitsperre des Handys länger',
    'main.intro.wakeFailed': '🔆 Bildschirm-wach-Halten wurde abgelehnt (Energiesparmodus?) — dein Handy dimmt vielleicht',
    'main.fix.none': '📍 noch kein GPS-Fix — tipp stattdessen auf die Karte',
    'main.fix.outside': '📍 deine Position liegt ausserhalb dieser Welt — tipp stattdessen auf die Karte',
    'main.fix.label': 'deine GPS-Position',
    'main.fix.waiting': '📍 warte auf einen GPS-Fix…',
    'main.pin.here': 'hier warst du',
    'main.quit.game': 'Welt verlassen?',
    'main.quit.map': 'Karte verlassen?',
    'main.quit.gameSub': 'Du verlässt die Welt und den Vollbildmodus; die Seite startet neu.',
    'main.quit.mapSub': 'Zurück zur Startseite, raus aus dem Vollbild.',
    'main.beam.inWorld': 'schon in der Welt',
    'main.beam.noMode': 'wähl zuerst auf der Startseite einen Modus',
    'main.beam.busy': 'beamt schon…',
    'main.flight.mapAfter': 'Karte nach der Landung',
    'main.flight.briefAfter': 'Briefing nach der Landung',
    'main.flight.holdSkip': 'Leertaste halten zum Überspringen',
    'main.flight.landing': 'Landung',
    'main.flight.alt': '{m} m ü. M.',
    'main.flight.water': '{lake} {c} °C',
    'main.wait.alt': 'Du landest auf {m} m ü. M.',
    'main.wait.peak': 'Nächster Gipfel: {name}, {m} m — {km} km {dir}',
    'main.wait.highest': 'Höchster ringsum: {name}, {m} m — {km} km {dir}',
    'main.wait.summits': '{n} benannte Gipfel im Umkreis von {km} km',
    'main.wait.lake': '{name} — {km} km {dir}',
    'main.wait.place': '{name} — {km} km {dir}',
    'main.dir.n': 'N', 'main.dir.ne': 'NO', 'main.dir.e': 'O', 'main.dir.se': 'SO',
    'main.dir.s': 'S', 'main.dir.sw': 'SW', 'main.dir.w': 'W', 'main.dir.nw': 'NW',
    'main.bench.run': 'BENCH · {phase} — Hände weg, es läuft von selbst',
    'main.bench.done': 'BENCH fertig — Bericht {id} gesendet. Das Ops-Panel zeigt ihn: Welt → Telemetry → Device bench.',
    'main.bench.noRoute': 'Bench: keine Route für diese Welt (bench/{world}.json) — normales Spiel',
    'main.beam.again': 'beame nochmal…',
    'main.beam.vrFail': 'VR-Sitzung fehlgeschlagen: {err} — noch auf der Karte',
    'main.beam.abandoned': 'Beam abgebrochen',
    'main.beam.refused': 'Beam vom Server abgelehnt (ausserhalb, Kampf oder ein Platz vor weniger als 2 s) — zweimal versucht, noch auf der Karte',
    'main.beam.timeout': 'keine Antwort vom Spielserver in {s} s, zweimal — noch auf der Karte',
    'main.beam.offline': 'nicht mit dem Spielserver verbunden — noch auf der Karte',
    'main.hud.down': '💀 AM BODEN — eingefroren {s}s',
    'main.hud.dizzy': '💨 SCHWINDLIG {s}s',
    'main.hud.jet': '🚀 JET {v} km/h · {h} m über Grund',
    'main.hud.bike': '🏍️ {v} km/h · {surf}',
    'main.hud.road': 'Strasse',
    'main.hud.offroad': 'Gelände',
    'main.hud.auto': 'AUTO',
    'main.hud.manual': 'MANUELL',
    'main.hud.indL': '⬅ BLINKER',
    'main.hud.indR': 'BLINKER ➡',
    'main.hud.funicular': '🚋 Standseilbahn in der Nähe — einfach einsteigen',
    'main.hud.chair': '🚡 Sessellift hier',
    'main.hud.cabin': '🚠 Kabine hier',
    'main.hud.orClick': 'oder anklicken',
    'main.hud.monoClick': '<kbd>3</kbd> Monokular, dann anklicken',
    'main.hud.keysBikeAuto': '<kbd>W</kbd>/<kbd>S</kbd> Gas/Bremse <kbd>←</kbd>/<kbd>→</kbd> Blinker <kbd>Q</kbd> manuell <kbd>B</kbd> absteigen',
    'main.hud.keysBikeMan': '<kbd>W</kbd>/<kbd>S</kbd> Gas/Bremse <kbd>A</kbd>/<kbd>D</kbd> lenken <kbd>Q</kbd> Auto <kbd>B</kbd> absteigen',
    'main.hud.keysGlide': 'flieg, wohin du schaust <kbd>S</kbd> bremst',
    'main.hud.keysCombat': '<kbd>1</kbd> Gewehr <kbd>2</kbd> Sniper <kbd>3</kbd> Monokular <kbd>Q</kbd>/<kbd>MMT</kbd> Wechseln',
    'main.hud.keyMic': '<kbd>M</kbd> Mikro',
    'main.hud.keyMicMuted': '<kbd>M</kbd> Mikro (stumm)',
    'main.hud.keyFire': '<kbd>LMT</kbd> Feuer',
    'main.hud.keyInspect': '<kbd>LMT</kbd> Infos',
    'main.kh.d.move': '<kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd> Gehen',
    'main.kh.d.look': '<kbd>Maus</kbd> Blick',
    'main.kh.d.sprint': '<kbd>Shift</kbd> Rennen',
    'main.kh.d.jump': '<kbd>Leertaste</kbd> Sprung',
    'main.kh.d.climb': '<kbd>E</kbd> Klettern',
    'main.kh.d.zoom': '<kbd>Mausrad</kbd> Zoom',
    'main.kh.d.map': '<kbd>RMT</kbd>/<kbd>N</kbd> Karte',
    'main.kh.d.bike': '<kbd>B</kbd> Bike',
    'main.kh.d.help': '<kbd>?</kbd> Hilfe',
    'main.kh.p.walk': 'Stick: gehen/rennen',
    'main.kh.p.walkTurn': 'Stick ↕ gehen ↔ drehen',
    'main.kh.p.lookPoint': 'Handy zeigen: Blick',
    'main.kh.p.lookBoth': 'zeigen/ziehen: Blick',
    'main.kh.p.lookDrag': 'ziehen: Blick',
    'main.kh.p.jump': '🚀 Sprung',
    'main.kh.p.zoom': 'Pinch/Slider: Zoom',
    'main.kh.p.mapTilt': 'flach kippen: Karte',
    'main.kh.p.mapBtn': '🗺 Karte',
    'main.kh.p.lock': '🔒 Sicht halten',
    'main.kh.p.inspect': '◎ was ist da',
    'main.kh.p.fire': 'FEUER · ⟳ Waffe',
    'main.kh.p.gps': '📍 GPS folgen',
    'main.kh.p.glide': 'Jet-Suit: fliegt, wohin du schaust',
    'main.kh.v.move': 'L-Stick gehen (voll: rennen)',
    'main.kh.v.turn': 'R-Stick ↔ drehen',
    'main.kh.v.jump': 'R-Klick Sprung/Jet',
    'main.kh.v.climb': 'Griffe klettern',
    'main.kh.v.zoom': 'R-Stick ↕ Zoom',
    'main.kh.v.map': 'L-Trigger Karte',
    'main.kh.v.point': 'R-Trigger: was ist da',
    'main.kh.v.menu': 'A Menü',
    'main.kh.v.bike': 'X Bike',
    'main.vr.keysBikeAuto': 'L-Stick ↕ Gas · ↔ Blinker antippen · Y manuell · X absteigen · A Menü',
    'main.vr.keysBikeMan': 'L-Stick ↕ Gas ↔ lenken · Y Auto-Folgen · X absteigen · A Menü',
    'main.vr.keysGlide': 'L-Stick zurück = steigen · vor = sinken · ↔ lenkt · Kopf schaut nur · A Menü',
    'main.vr.zonePoint': 'R-Trigger',
    'main.vr.mic': 'Y Mikro',
    'main.vr.micMuted': 'Y Mikro (stumm)',
    'main.gl.restoring': 'Grafik zurückgesetzt — stelle wieder her…',
    'main.gl.lost': 'Grafik zurückgesetzt — nicht wiederhergestellt',
    'main.gl.restored': 'Grafik wieder da — fehlt etwas?',
    'main.html.beaming': '⤳ beame…',
    'main.html.mapTitle': 'Karte (RMT / N)',
    'main.html.map2d': '2D-Karte (Rechtsklick / N)',
    'main.html.help': 'So funktioniert’s',
    'main.html.me': 'Auf mich zentrieren',
    'main.html.quit': 'Beenden',
    'main.html.exit': '⏻ Beenden',
    'main.html.mapClose': 'Schliessen (RMT / N / Esc)',
    'main.html.mtbTitle': 'MTB: S-Skala-Farben, MTB- + Velorouten',
    'main.html.amen': '🚰 Infrastruktur',
    'main.html.amenTitle': 'Bänke, Brunnen, Toiletten, Unterstände …',
    'main.html.north': 'Norden oben — tipp für Blickrichtung oben',
    'main.html.zoomIn': 'Reinzoomen',
    'main.html.zoomOut': 'Rauszoomen',
    'main.html.inspect': 'Was das Fernglas sieht',
    'main.html.stay': 'Abbrechen',
    'main.html.leave': 'Beenden',
    'main.html.helpQ': 'So funktioniert’s (?)',
    'main.html.briefing': 'Briefing',
    'main.html.close': 'Schliessen',
    'main.html.closeEsc': 'Schliessen (Esc)',
    'main.html.beamFix': '📍 Beam zu meiner Position',
    'main.html.gotIt': 'Alles klar',
    'main.html.reloadHere': '⟳ hier neu laden',
  },
});
